import { ExternalLink, Quote } from "lucide-react";
import Reveal from "@/components/books/Reveal";

/**
 * TrustReviews — reusable trust/reviews block, PREPARED FOR FUTURE USE and
 * currently DISABLED. Renders absolutely nothing (no empty state, no
 * "coming soon" placeholder) unless BOTH `enabled` is true AND `items`
 * contains at least one entry with `consent_status === "approved"`.
 *
 * Never invent or paraphrase testimonial content — display owner-approved
 * wording and attribution exactly as supplied.
 *
 * Supported item shapes (see cyberSafetyBooks.js for the full schema doc):
 *  early_reader | professional_review | school_feedback | media_mention
 */
export default function TrustReviews({ items = [], enabled = false, accentColor = "#D4A030", navyColor = "#0D1B2A", mutedColor = "#5A6B82", testId = "trust-reviews-section" }) {
  const approved = enabled ? items.filter((it) => it?.consent_status === "approved") : [];
  if (!approved.length) return null;

  return (
    <section data-testid={testId} className="bg-white">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: accentColor }}>Early feedback</div>
        <h2 className="mt-5 font-serif-d text-[32px] sm:text-5xl leading-[1.08]" style={{ color: navyColor }}>What readers are saying.</h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {approved.map((item, i) => (
            <Reveal key={`${item.type}-${i}`} delay={i * 0.06}>
              <div data-testid={`trust-review-${i}`} className="h-full border rounded-sm p-6 sm:p-7" style={{ borderColor: `${navyColor}1A` }}>
                {item.type === "media_mention" ? (
                  <div>
                    {item.logo ? (
                      <img src={item.logo} alt={item.publication} className="h-8 object-contain" />
                    ) : (
                      <div className="font-serif-d text-[18px]" style={{ color: navyColor }}>{item.publication}</div>
                    )}
                    <div className="mt-3 text-[13px]" style={{ color: mutedColor }}>{item.date}</div>
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide" style={{ color: accentColor }}>
                        Read the mention <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                ) : (
                  <div>
                    <Quote size={20} style={{ color: accentColor }} />
                    <p className="mt-3 font-serif-d italic text-[16px] sm:text-[17px] leading-[1.55]" style={{ color: navyColor }}>
                      &ldquo;{item.quote}&rdquo;
                    </p>
                    <div className="mt-4 text-[13px] font-semibold" style={{ color: mutedColor }}>
                      {item.type === "school_feedback" ? (
                        <>{item.attribution}{item.organisation ? `, ${item.organisation}` : ""}</>
                      ) : item.type === "professional_review" ? (
                        <>{item.name}{item.designation ? `, ${item.designation}` : ""}{item.organisation ? `, ${item.organisation}` : ""}</>
                      ) : (
                        <>{item.name}{item.role ? `, ${item.role}` : ""}</>
                      )}
                    </div>
                    {item.source && <div className="mt-1 text-[12px]" style={{ color: mutedColor }}>{item.source}{item.date ? ` · ${item.date}` : ""}</div>}
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
